import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, ArrowRight, ArrowLeftRight, Banknote, Smartphone, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { devWarn } from "@/lib/log";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Dashboard widget — dual-balance petty cash pill for spenders.
//
// Shows both Cash + UPI balances distinctly with a shortcut to swap funds
// between them (matches the admin's own transfer button). Renders nothing
// for non-spenders (admins, users without ``wallet_enabled``).
// ---------------------------------------------------------------------------
function money(n) {
  const v = Number(n) || 0;
  return "₹" + v.toLocaleString("en-IN", { minimumFractionDigits: v % 1 ? 2 : 0 });
}

export default function WalletBalanceWidget() {
  const { user } = useAuth();
  const [state, setState] = useState({ enabled: false, cash_balance: 0, upi_balance: 0, recent: [] });
  const [loaded, setLoaded] = useState(false);

  // Transfer dialog
  const [xferOpen, setXferOpen] = useState(false);
  const [xferFrom, setXferFrom] = useState("cash");
  const [xferAmount, setXferAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/wallets/me");
      setState(data || { enabled: false, cash_balance: 0, upi_balance: 0, recent: [] });
    } catch (e) {
      devWarn("wallet widget load failed", e?.message);
    } finally { setLoaded(true); }
  };

  useEffect(() => { load(); }, []);

  const submitTransfer = async () => {
    const n = Number(xferAmount);
    if (!user?.id || !(n > 0)) { toast.error("Amount must be positive"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/wallets/${user.id}/transfer`, {
        amount:    n,
        from_mode: xferFrom,
      });
      toast.success(`${money(n)} ${data.from_mode.toUpperCase()} → ${data.to_mode.toUpperCase()}`);
      setXferOpen(false);
      setXferAmount("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Transfer failed"));
    } finally { setSaving(false); }
  };

  if (!loaded || !state.enabled) return null;

  const cash = Number(state.cash_balance) || 0;
  const upi  = Number(state.upi_balance)  || 0;

  return (
    <div className="border-2 border-[#0D0D0D] bg-white p-4 mb-6" data-testid="wallet-balance-widget">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-full bg-neutral-100 flex items-center justify-center">
          <Wallet className="h-5 w-5 text-[#0D0D0D]" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Petty cash</div>
          <div className="text-xs text-neutral-500">Total <b className="text-[#0D0D0D] tabular-nums">{money(cash + upi)}</b></div>
        </div>
        <button
          onClick={() => setXferOpen(true)}
          data-testid="wallet-widget-transfer"
          className="h-9 px-3 text-[11px] font-bold uppercase tracking-widest bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1"
        >
          <ArrowLeftRight className="h-3 w-3" /> Transfer
        </button>
        <Link
          to="/expenses/new"
          data-testid="wallet-widget-log-expense"
          className="h-9 px-3 text-[11px] font-bold uppercase tracking-widest border border-[#0D0D0D] hover:bg-[#0D0D0D] hover:text-white flex items-center gap-1"
        >
          Log <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className={`border-2 p-3 ${cash < 0 ? "border-[#DC262F] bg-red-50" : "border-emerald-600 bg-emerald-50 dark:bg-emerald-950/30"}`} data-testid="wallet-widget-cash">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-emerald-800 dark:text-emerald-300">
            <Banknote className="h-3.5 w-3.5" /> Cash
          </div>
          <div className={`mt-1 text-2xl font-black tabular-nums ${cash < 0 ? "text-[#DC262F]" : "text-emerald-700 dark:text-emerald-300"}`}>
            {money(cash)}
          </div>
        </div>
        <div className={`border-2 p-3 ${upi < 0 ? "border-[#DC262F] bg-red-50" : "border-indigo-600 bg-indigo-50 dark:bg-indigo-950/30"}`} data-testid="wallet-widget-upi">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-indigo-800 dark:text-indigo-300">
            <Smartphone className="h-3.5 w-3.5" /> UPI
          </div>
          <div className={`mt-1 text-2xl font-black tabular-nums ${upi < 0 ? "text-[#DC262F]" : "text-indigo-700 dark:text-indigo-300"}`}>
            {money(upi)}
          </div>
        </div>
      </div>

      {/* Transfer dialog (self) */}
      <Dialog open={xferOpen} onOpenChange={(v) => !saving && setXferOpen(v)}>
        <DialogContent className="max-w-md rounded-none border-[#0D0D0D]" data-testid="wallet-widget-xfer-dialog">
          <DialogTitle className="font-heading text-2xl">Transfer Cash ↔ UPI</DialogTitle>
          <DialogDescription className="text-sm text-neutral-600">
            Cash <b>{money(cash)}</b> · UPI <b>{money(upi)}</b>
          </DialogDescription>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Move from</label>
              <div className="mt-1 flex border border-[#0D0D0D]">
                {[
                  { v: "cash", label: "Cash → UPI", icon: <Banknote className="h-3.5 w-3.5" /> },
                  { v: "upi",  label: "UPI → Cash", icon: <Smartphone className="h-3.5 w-3.5" /> },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setXferFrom(opt.v)}
                    data-testid={`wallet-widget-xfer-from-${opt.v}`}
                    className={`flex-1 h-11 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest transition-colors ${
                      xferFrom === opt.v ? "bg-indigo-600 text-white" : "bg-white hover:bg-neutral-50 text-neutral-500"
                    }`}
                  >
                    {opt.icon} {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Amount (₹)</label>
              <input
                type="number"
                inputMode="decimal"
                value={xferAmount}
                onChange={(e) => setXferAmount(e.target.value)}
                autoFocus
                placeholder="e.g. 200"
                data-testid="wallet-widget-xfer-amount"
                className="mt-1 w-full h-12 rounded-none border border-[#0D0D0D] bg-white px-3 text-2xl font-black tabular-nums"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setXferOpen(false)}
                disabled={saving}
                className="h-10 px-4 border border-[#0D0D0D] text-xs font-bold uppercase tracking-widest disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={submitTransfer}
                disabled={saving || !(Number(xferAmount) > 0)}
                data-testid="wallet-widget-xfer-save"
                className="h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-widest disabled:opacity-40 flex items-center gap-2"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowLeftRight className="h-3.5 w-3.5" />}
                Transfer
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
